import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import type { ChatMessageData } from '../_utils/message';
const BOTTOM_STICKY_THRESHOLD = 180;
const USER_SCROLL_DELTA = 2;
const FOLLOW_EASE = 0.25;
const SETTLE_THRESHOLD_PX = 1;
const SNAP_THRESHOLD_PX = 4;
const GLIDE_RELEASE_PX = 4;
const MOBILE_TABLET_MAX_WIDTH = 1279;
const MOBILE_HEADER_HEIGHT = 48;
const DESKTOP_HEADER_HEIGHT = 72;
const SCROLL_SAFE_GAP = 16;
const getBottomAlign = () => document.documentElement.scrollHeight - window.innerHeight;
const isNearBottom = () => getBottomAlign() - window.scrollY <= BOTTOM_STICKY_THRESHOLD;
const getTopOffset = () => {
  const isDesktop = window.matchMedia(`(min-width: ${MOBILE_TABLET_MAX_WIDTH + 1}px)`).matches;
  return (isDesktop ? DESKTOP_HEADER_HEIGHT : MOBILE_HEADER_HEIGHT) + SCROLL_SAFE_GAP;
};
const getAnchorTop = (messageId: string) => {
  const element = document.getElementById(`message-${messageId}`);
  if (!element) {
    return null;
  }
  return Math.max(0, window.scrollY + element.getBoundingClientRect().top - getTopOffset());
};
export const useStickToLatestMessage = messages => {
  const $ = _c(16);
  const shouldStickRef = useRef(true);
  const lastTouchYRef = useRef(null);
  const rafRef = useRef(null);
  const glideMessageIdRef = useRef(null);
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);
  let t0;
  if ($[0] !== messages) {
    t0 = [...messages].reverse().find(_temp)?.id ?? null;
    $[0] = messages;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const latestUserMessageId = t0;
  let t1;
  let t2;
  if ($[2] !== latestUserMessageId) {
    t1 = () => {
      if (!latestUserMessageId) {
        return;
      }
      glideMessageIdRef.current = latestUserMessageId;
      shouldStickRef.current = true;
    };
    t2 = [latestUserMessageId];
    $[2] = latestUserMessageId;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const stopSticking = () => {
        shouldStickRef.current = false;
        glideMessageIdRef.current = null;
      };
      const resumeSticking = () => {
        if (isNearBottom()) {
          shouldStickRef.current = true;
        }
      };
      const handleWheel = event => {
        if (event.deltaY < -USER_SCROLL_DELTA) {
          stopSticking();
        } else {
          if (event.deltaY > USER_SCROLL_DELTA) {
            resumeSticking();
          }
        }
      };
      const handleTouchStart = event_0 => {
        lastTouchYRef.current = event_0.touches[0]?.clientY ?? null;
      };
      const handleTouchMove = event_1 => {
        const currentTouchY = event_1.touches[0]?.clientY;
        const lastTouchY = lastTouchYRef.current;
        if (currentTouchY === undefined || lastTouchY === null) {
          return;
        }
        if (currentTouchY > lastTouchY + USER_SCROLL_DELTA) {
          stopSticking();
        } else {
          if (currentTouchY < lastTouchY - USER_SCROLL_DELTA) {
            resumeSticking();
          }
        }
        lastTouchYRef.current = currentTouchY;
      };
      const handleKeyDown = event_2 => {
        if (["ArrowUp", "PageUp", "Home"].includes(event_2.key)) {
          stopSticking();
        }
        if (event_2.key === "End" || event_2.key === "ArrowDown" && isNearBottom()) {
          shouldStickRef.current = true;
        }
      };
      const handleScroll = () => {
        setShowJumpToBottom(!isNearBottom());
      };
      window.addEventListener("wheel", handleWheel, {
        passive: true
      });
      window.addEventListener("touchstart", handleTouchStart, {
        passive: true
      });
      window.addEventListener("touchmove", handleTouchMove, {
        passive: true
      });
      window.addEventListener("keydown", handleKeyDown);
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => {
        window.removeEventListener("wheel", handleWheel);
        window.removeEventListener("touchstart", handleTouchStart);
        window.removeEventListener("touchmove", handleTouchMove);
        window.removeEventListener("keydown", handleKeyDown);
        window.removeEventListener("scroll", handleScroll);
      };
    };
    t4 = [];
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== messages.length) {
    t5 = () => {
      if (!messages.length || !shouldStickRef.current || rafRef.current !== null) {
        return;
      }
      const followFrame = () => {
        if (!shouldStickRef.current) {
          rafRef.current = null;
          return;
        }
        const bottomAlign = getBottomAlign();
        const glideMessageId = glideMessageIdRef.current;
        let target = bottomAlign;
        if (glideMessageId) {
          const anchorTop = getAnchorTop(glideMessageId);
          if (anchorTop === null || bottomAlign > anchorTop + GLIDE_RELEASE_PX) {
            glideMessageIdRef.current = null;
          } else {
            target = Math.min(anchorTop, bottomAlign);
          }
        }
        const currentScrollY = window.scrollY;
        const delta = target - currentScrollY;
        if (Math.abs(delta) <= SETTLE_THRESHOLD_PX) {
          rafRef.current = null;
          return;
        }
        const nextScrollY = Math.abs(delta) <= SNAP_THRESHOLD_PX ? target : currentScrollY + delta * FOLLOW_EASE;
        window.scrollTo(0, nextScrollY);
        rafRef.current = window.requestAnimationFrame(followFrame);
      };
      rafRef.current = window.requestAnimationFrame(followFrame);
    };
    $[7] = messages.length;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== messages) {
    t6 = [messages];
    $[9] = messages;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => () => {
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    t8 = [];
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  useEffect(t7, t8);
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      shouldStickRef.current = true;
      glideMessageIdRef.current = null;
      window.scrollTo(0, getBottomAlign());
      setShowJumpToBottom(false);
    };
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const jumpToBottom = t9;
  let t10;
  if ($[14] !== showJumpToBottom) {
    t10 = {
      showJumpToBottom,
      jumpToBottom
    };
    $[14] = showJumpToBottom;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  return t10;
};
function _temp(message) {
  return message.type === "user";
}